import { Button } from '@app/components';
import { Link, useNavigate } from 'react-router-dom';
import { useSignUpPage } from './SignUp.hooks';
import {
  BusinessCategoryInterface,
  PlaceInterface,
} from '@app/models/business';
import { searchPlaces } from '@app/services/business';
import GoogleLogo from '@app/assets/icons/Google.svg?react';
import Logo from '@app/assets/vertical-logo.svg?react';
import NoCampaignsIcon from '@app/assets/illustrations/no-campaigns.svg?react';
import { WELCOME_FEATURES } from '@app/utils/consts';

import { useState, useEffect, useRef } from 'react';
import { verifyPasswordEmail } from '@app/services/auth';

export const SignUp = () => {
  const navigate = useNavigate();
  const {
    signupForm,
    submitGoogleSignup,
    submitInviteSignup,
    isPending,
    isInviteSignupPending,
    isLoading,
    businessCategories,
    handleEmailValidation,
    isValidatingEmail,
    emailAlreadyExists,
    clearEmailValidationError,
    handleBusinessEmailValidation,
    isValidatingBusinessEmail,
    businessEmailError,
    clearBusinessEmailValidationError,
  } = useSignUpPage();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<'plan' | 'free'>('plan');
  const [selectedPlan, setSelectedPlan] = useState<
    'starter' | 'professional' | 'enterprise'
  >('professional');
  const [countdown, setCountdown] = useState(56);


  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    businessEmail: '',
    businessAddress: '',
    businessCategory: '',
    teamSize: '',
    terms: false,
  });

  // Check if user is signing up via invite
  const pendingInviteToken = localStorage.getItem('pendingInviteToken');
  const pendingInviteEmail = localStorage.getItem('pendingInviteEmail');
  const isInviteSignup = !!pendingInviteToken;

  // Pre-fill email from invite on component mount
  useEffect(() => {
    if (pendingInviteEmail) {
      setFormData((prev) => {
        if (!prev.email) {
          return { ...prev, email: pendingInviteEmail };
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (businessEmailError) {
      clearBusinessEmailValidationError();
    }
  }, [formData.businessEmail]);


  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailFromUrl = urlParams.get('email');
    const nameFromUrl = urlParams.get('name');
    const stepFromUrl = urlParams.get('step');

    if (emailFromUrl || nameFromUrl) {
      setFormData((prev) => ({
        ...prev,
        ...(emailFromUrl && { email: emailFromUrl }),
        ...(nameFromUrl && { fullName: decodeURIComponent(nameFromUrl) }),
      }));
    }

    // Set current step from URL parameter
    if (stepFromUrl) {
      const step = parseInt(stepFromUrl, 10);
      if (step >= 1 && step <= 5) {
        setCurrentStep(step);

        // If navigating to step 4 and no email in URL, try to get it from localStorage
        if (step === 4 && !emailFromUrl) {
          try {
            const storedFormData = localStorage.getItem('signupFormData');
            if (storedFormData) {
              const parsedFormData = JSON.parse(storedFormData);
              if (parsedFormData.email) {
                setFormData((prev) => ({
                  ...prev,
                  email: parsedFormData.email,
                  fullName: parsedFormData.fullName || prev.fullName,
                  businessName:
                    parsedFormData.businessName || prev.businessName,
                  businessAddress:
                    parsedFormData.businessAddress || prev.businessAddress,
                  businessCategory:
                    parsedFormData.businessCategory || prev.businessCategory,
                  teamSize: parsedFormData.teamSize || prev.teamSize,
                }));
              }
            }
          } catch (error) {
            console.error('Error retrieving stored form data:', error);
          }
        }
      }
    }
  }, []);

  // Countdown timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [countdown]);

  // Reset countdown when entering step 4
  useEffect(() => {
    if (currentStep === 4) {
      setCountdown(56);
    }
  }, [currentStep]);

  const pricingPlans = [
    {
      id: 'starter' as const,
      name: 'Starter Plan',
      price: '$47/mo',
      audience: 'For Small Businesses and Local Brands',
      features: [
        { text: '1 Member seats', included: true },
        { text: 'Up to 300 outreach attempts', included: true },
        { text: 'Synchronization w/ Leading Review platforms', included: true },
        { text: 'SMS/Email Review Outreach', included: true },
        { text: 'Non-public feedback & guided review process', included: true },
        { text: 'Centralized review Management Dashboard', included: true },
        { text: 'No Review Analytics', included: false },
      ],
      popular: false,
      bestValue: false,
    },
    {
      id: 'professional' as const,
      name: 'Professional Plan',
      price: '$97/mo',
      audience: 'For Growing Teams and Multi-Location Businesses',
      features: [
        { text: 'Up to 5 Member Seats', included: true },
        { text: 'Up to 1000 outreach attempts', included: true },
        { text: 'Synchronization w/ Leading Review platforms', included: true },
        { text: 'SMS/Email Review Outreach', included: true },
        { text: 'Non-public feedback & guided review process', included: true },
        { text: 'Centralized review Management Dashboard', included: true },
        { text: 'Basic review analytics', included: true },
      ],
      popular: true,
      bestValue: false,
    },
    {
      id: 'enterprise' as const,
      name: 'Enterprise Plan',
      price: '$127/mo',
      audience: 'For Enterprises, Agencies, and Franchises',
      features: [
        { text: 'Up to 20 Member Seats', included: true },
        { text: 'Up to 1000 outreach attempts', included: true },
        { text: 'Synchronization w/ Leading Review platforms', included: true },
        { text: 'SMS/Email Review Outreach', included: true },
        { text: 'Non-public feedback & guided review process', included: true },
        { text: 'Centralized review Management Dashboard', included: true },
        { text: 'Advanced review analytics', included: true },
      ],
      popular: false,
      bestValue: true,
    },
  ];

  const [isProcessingEmailValidation, setIsProcessingEmailValidation] =
    useState(false);
  const hasRedirected = useRef(false);

  // Business name search functionality
  const [places, setPlaces] = useState<PlaceInterface[]>([]);
  const [showPlacesDropdown, setShowPlacesDropdown] = useState(false);
  const [isSearchingPlaces, setIsSearchingPlaces] = useState(false);
  const [userLocation, setUserLocation] = useState<string>('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check for validateEmail parameter
  const urlParams = new URLSearchParams(window.location.search);
  const validateEmail = urlParams.get('validateEmail');

  // Password validation functions
  const validatePassword = (password: string) => {
    return {
      minLength: password.length >= 8,
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };
  };

  const getPasswordValidation = () => {
    return validatePassword(formData.password);
  };

  const isPasswordValid = () => {
    const validation = getPasswordValidation();
    return Object.values(validation).every(Boolean);
  };

  const doPasswordsMatch = () => {
    return (
      formData.password === formData.confirmPassword &&
      formData.confirmPassword.length > 0
    );
  };

  const isStep2Valid = () => {
    return isPasswordValid() && doPasswordsMatch();
  };

  // For invite signup, we skip the business info step (step 3)
  const totalSteps = isInviteSignup ? 4 : 5;

  // Handle email validation redirect
  useEffect(() => {
    if (validateEmail && !hasRedirected.current) {
      hasRedirected.current = true;
      setIsProcessingEmailValidation(true);

      // Check if this is an invite signup
      const storedFormData = localStorage.getItem('signupFormData');

      if (storedFormData) {
        try {
          const parsedFormData = JSON.parse(storedFormData);

          // Check if this was an invite signup (either by flag or by pending token)
          const pendingInviteToken = localStorage.getItem('pendingInviteToken');
          if (parsedFormData.isInviteSignup || pendingInviteToken) {
            // Invite signup flow - create user without business
            submitInviteSignup(parsedFormData);
          } else {
            // Normal signup flow - redirect to welcome page
            navigate('/welcome?validateEmail=' + validateEmail);
          }
        } catch (error) {
          console.error('Error processing signup:', error);
          setIsProcessingEmailValidation(false);
          navigate('/welcome?validateEmail=' + validateEmail);
        }
      } else {
        // No stored form data - redirect to welcome page
        navigate('/welcome?validateEmail=' + validateEmail);
      }
    }
  }, [validateEmail, navigate, submitInviteSignup]);

  // Handle Google OAuth callback and auto-fill form
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const googleAccessToken = urlParams.get('googleAccessToken');
    const email = urlParams.get('email');
    const userName = urlParams.get('userName');

    // Handle Google access token redirect with user data (new format)
    if (googleAccessToken) {
      localStorage.setItem('googleAccessToken', googleAccessToken);

      // Auto-fill form with user data if provided
      if (email) {
        signupForm.setValue('email', email);
        updateFormData('email', email);
      }
      if (userName) {
        signupForm.setValue('fullName', userName);
        updateFormData('fullName', userName);
      }

      // Clear the URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('googleAccessToken');
      newUrl.searchParams.delete('email');
      newUrl.searchParams.delete('userName');
      window.history.replaceState({}, document.title, newUrl.toString());
    }

    if (code && state) {
      // This means we're returning from Google OAuth callback
      // Call the callback endpoint to get user data
      fetch(
        `${import.meta.env.VITE_APP_API_BASE}/auth/google/register/callback?code=${code}&state=${state}`
      )
        .then((response) => response.json())
        .then((data) => {
          if (data?.data) {
            // Store the Google access token
            if (data.data.accessToken) {
              localStorage.setItem('googleAccessToken', data.data.accessToken);
            }

            // Auto-fill the form with user data
            if (data.data.userName) {
              signupForm.setValue('fullName', data.data.userName);
              updateFormData('fullName', data.data.userName);
            }
            if (data.data.email) {
              signupForm.setValue('email', data.data.email);
              updateFormData('email', data.data.email);
            }

            // Clear the URL parameters
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname
            );
          }
        })
        .catch((error) => {
          console.error('Error processing Google OAuth callback:', error);
        });
    }
  }, [signupForm]);

  // Sync form data with form values when they change (e.g., after Google signup)
  useEffect(() => {
    const subscription = signupForm.watch((value, { name }) => {
      if (name) {
        updateFormData(name, value[name]);
      }
    });
    return () => subscription.unsubscribe();
  }, [signupForm]);

  // Get user location on component mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation(`${latitude},${longitude}`);
        },
        (error) => {
          console.error('Error getting location:', error);
          // Fallback to a default location (San Francisco)
          setUserLocation('37.7749,-122.4194');
        }
      );
    } else {
      // Fallback to a default location if geolocation is not supported
      setUserLocation('37.7749,-122.4194');
    }
  }, []);

  // Search places when business name changes
  const handleBusinessNameChange = (value: string) => {
    updateFormData('businessName', value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Only search if query is longer than 3 characters and we have location
    if (value.length > 3 && userLocation) {
      // Debounce the search
      searchTimeoutRef.current = setTimeout(async () => {
        setIsSearchingPlaces(true);
        try {
          const response = await searchPlaces(value, userLocation);
          setPlaces(response.data.places || []);
          setShowPlacesDropdown(true);
        } catch (error) {
          console.error('Error searching places:', error);
          setPlaces([]);
        } finally {
          setIsSearchingPlaces(false);
        }
      }, 500); // 500ms debounce
    } else {
      setPlaces([]);
      setShowPlacesDropdown(false);
    }
  };

  // Handle place selection
  const handlePlaceSelect = (place: PlaceInterface) => {
    updateFormData('businessName', place.name);
    updateFormData('businessAddress', place.address);
    setShowPlacesDropdown(false);
    setPlaces([]);
  };

  // Clear email validation error when email changes
  useEffect(() => {
    if (emailAlreadyExists) {
      clearEmailValidationError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.email]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Show loading screen if processing email validation (must be after all hooks)
  if (isProcessingEmailValidation || isInviteSignupPending) {
    const inviteToken = localStorage.getItem('pendingInviteToken');
    const message = inviteToken
      ? 'Creating your account...'
      : 'Redirecting to plan selection...';

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-baseBlue mx-auto mb-4"></div>
          <p className="text-base-coolGray">{message}</p>
        </div>
      </div>
    );
  }

  const renderProgressBar = () => {
    return (
      <div className="flex gap-2 mb-6">
        {Array.from({ length: totalSteps }, (_, index) => (
          <div
            key={index}
            className={`h-1 flex-1 rounded ${
              index < currentStep
                ? index === currentStep - 1
                  ? 'bg-[#3279C5]'
                  : 'bg-brand-baseBlue'
                : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
    );
  };

  const handleNextStep = async () => {
    // Validate email when moving from step 1 to step 2
    if (currentStep === 1) {
      const isValid = await handleEmailValidation(formData.email);

      // Only proceed if validation passed (returns true)
      if (isValid && currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
      // If validation failed (returns false), the hook handles showing error and redirect
    } else if (currentStep === 2 && isInviteSignup) {
      // For invite signup, skip business info (step 3) and go to email verification
      handleInviteSignupSubmit();
    } else {
      // For other steps, just proceed normally
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleInviteSignupSubmit = async () => {
    // For invite signup, skip email verification and register directly
    submitInviteSignup({
      ...formData,
      isInviteSignup: true,
      inviteToken: localStorage.getItem('pendingInviteToken'),
    });
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinalSubmit = async () => {
    try {
      const isBusinessEmailFree = await handleBusinessEmailValidation(
        formData.businessEmail
      );

      if (!isBusinessEmailFree) {
        return;
      }

      localStorage.setItem('signupFormData', JSON.stringify(formData));

      await verifyPasswordEmail(formData.email);

      setCurrentStep(4);
    } catch (error) {
      console.error('Error in final submit:', error);
    }
  };

  const handleResendEmail = async () => {
    try {
      await verifyPasswordEmail(formData.email);
      setCountdown(56); // Reset countdown when email is resent
    } catch (error) {
      console.error('Error resending email:', error);
    }
  };

  const updateFormData = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Validation functions
  const validateFullName = (name: string) => {
    const nameParts = name.trim().split(' ');
    return nameParts.length >= 2 && nameParts.every((part) => part.length > 0);
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getFullNameError = () => {
    if (!formData.fullName.trim()) return '';
    return validateFullName(formData.fullName)
      ? ''
      : 'Please write your first and last name';
  };

  const getEmailError = () => {
    if (!formData.email.trim()) return '';
    return isValidEmail(formData.email)
      ? ''
      : 'Please enter a valid email address';
  };

  // Business information validation functions
  const hasStartedBusinessForm = () => {
    return (
      formData.businessName.trim() ||
      formData.businessAddress.trim() ||
      formData.businessCategory ||
      formData.teamSize ||
      formData.terms
    );
  };

  const getBusinessNameError = () => {
    if (!hasStartedBusinessForm()) return '';
    if (!formData.businessName.trim()) return 'Enter Business name';
    return '';
  };

  const getBusinessAddressError = () => {
    if (!hasStartedBusinessForm()) return '';
    if (!formData.businessAddress.trim()) return 'Enter Business address';
    return '';
  };

  const getBusinessCategoryError = () => {
    if (!hasStartedBusinessForm()) return '';
    if (!formData.businessCategory) return 'Select Industry';
    return '';
  };

  const getTeamSizeError = () => {
    if (!hasStartedBusinessForm()) return '';
    if (!formData.teamSize) return 'Select Team Size';
    return '';
  };

  const getTermsError = () => {
    if (!hasStartedBusinessForm()) return '';
    if (!formData.terms) return 'Field is required';
    return '';
  };

  const renderStep1 = () => {
    const fullNameError = getFullNameError();
    const emailError = getEmailError();

    return (
      <>
        <h2 className="font-bold text-2xl mb-2">Create Account</h2>
        <p className="text-base-coolGray mb-6">
          Let's get started! Enter your full name and email
        </p>
        <div className="flex gap-4 flex-col">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Full Name
            </label>
            <input
              type="text"
              placeholder="e.g. Jacob Jones"
              className={`w-full bg-base-seasalt rounded py-3 px-4 ${
                fullNameError
                  ? 'border border-red-500 outline-none'
                  : 'outline-brand-baseBlue outline-1'
              }`}
              value={formData.fullName}
              onChange={(e) => updateFormData('fullName', e.target.value)}
            />
            {fullNameError && (
              <p className="text-red-500 text-sm mt-1 text-left">
                {fullNameError}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Email
            </label>
            <input
              type="email"
              placeholder="e.g. user@example.com"
              className={`w-full bg-base-seasalt rounded py-3 px-4 ${
                emailError || emailAlreadyExists
                  ? 'border border-red-500 outline-none'
                  : 'outline-brand-baseBlue outline-1'
              }`}
              value={formData.email}
              onChange={(e) => updateFormData('email', e.target.value)}
            />
            {emailError && (
              <p className="text-red-500 text-sm mt-1 text-left">
                {emailError}
              </p>
            )}
            {emailAlreadyExists && (
              <p className="text-red-500 text-sm mt-1 text-left">
                This email is already registered. Redirecting to login...
              </p>
            )}
          </div>
          <Button
            onClick={handleNextStep}
            className="w-full"
            disabled={
              !formData.fullName.trim() ||
              !formData.email.trim() ||
              !!fullNameError ||
              !!emailError ||
              isValidatingEmail ||
              emailAlreadyExists
            }
            loading={isValidatingEmail}
          >
            Continue
          </Button>
        </div>
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[#9CA3AF]"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-[#9CA3AF]">or</span>
          </div>
        </div>
        <Button
          onClick={() => submitGoogleSignup()}
          type="button"
          kind="tertiary"
          className="border w-full"
          disabled={isPending}
          loading={isPending || isLoading}
          leftNode={<GoogleLogo />}
        >
          Continue with Google
        </Button>
      </>
    );
  };

  const renderStep2 = () => {
    const passwordValidation = getPasswordValidation();
    const passwordError = !isPasswordValid() && formData.password.length > 0;
    const confirmPasswordError =
      !doPasswordsMatch() && formData.confirmPassword.length > 0;

    return (
      <>
        <h2 className="font-bold text-2xl mb-2">Enter Password</h2>
        <p className="text-base-coolGray mb-6">
          Great! Now, enter your password
        </p>
        <div className="flex gap-4 flex-col">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="8+ characters"
                className={`w-full bg-base-seasalt rounded py-3 px-4 pr-10 min-w-[400px] ${
                  passwordError
                    ? 'border border-red-500 outline-none'
                    : 'outline-brand-baseBlue outline-1'
                }`}
                value={formData.password}
                onChange={(e) => updateFormData('password', e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d={
                      showPassword
                        ? 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21'
                        : 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                    }
                  />
                </svg>
              </button>
            </div>
            {passwordError && (
              <p className="text-red-500 text-sm mt-1 text-left">
                Weak password.
              </p>
            )}

            {/* Password validation checklist */}
            <div className="space-y-2 mt-3">
              <div
                className={`flex items-center gap-2 text-sm ${
                  passwordValidation.minLength
                    ? 'text-green-600'
                    : 'text-red-500'
                }`}
              >
                {passwordValidation.minLength ? (
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
                Minimum 8 characters
              </div>
              <div
                className={`flex items-center gap-2 text-sm ${
                  passwordValidation.hasLowercase
                    ? 'text-green-600'
                    : 'text-red-500'
                }`}
              >
                {passwordValidation.hasLowercase ? (
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
                At least one lowercase letter
              </div>
              <div
                className={`flex items-center gap-2 text-sm ${
                  passwordValidation.hasUppercase
                    ? 'text-green-600'
                    : 'text-red-500'
                }`}
              >
                {passwordValidation.hasUppercase ? (
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
                At least one uppercase letter
              </div>
              <div
                className={`flex items-center gap-2 text-sm ${
                  passwordValidation.hasNumber
                    ? 'text-green-600'
                    : 'text-red-500'
                }`}
              >
                {passwordValidation.hasNumber ? (
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
                At least one number
              </div>
              <div
                className={`flex items-center gap-2 text-sm ${
                  passwordValidation.hasSpecialChar
                    ? 'text-green-600'
                    : 'text-red-500'
                }`}
              >
                {passwordValidation.hasSpecialChar ? (
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
                At least one special character (!@#$%^&*...)
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="8+ characters"
                className={`w-full bg-base-seasalt rounded py-3 px-4 pr-10 min-w-[400px] ${
                  confirmPasswordError
                    ? 'border border-red-500 outline-none'
                    : 'outline-brand-baseBlue outline-1'
                }`}
                value={formData.confirmPassword}
                onChange={(e) =>
                  updateFormData('confirmPassword', e.target.value)
                }
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d={
                      showConfirmPassword
                        ? 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21'
                        : 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                    }
                  />
                </svg>
              </button>
            </div>
            {confirmPasswordError && (
              <p className="text-red-500 text-sm mt-1 text-left">
                Passwords do not match
              </p>
            )}
          </div>

          <Button
            onClick={handleNextStep}
            className="w-full min-w-[400px]"
            disabled={!isStep2Valid()}
          >
            Continue
          </Button>
          <button
            onClick={handlePreviousStep}
            className="w-full min-w-[400px] bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <span>←</span>
            Previous Step
          </button>
        </div>
      </>
    );
  };

  const renderStep2Invite = () => {
    const passwordValidation = getPasswordValidation();
    const passwordError = !isPasswordValid() && formData.password.length > 0;
    const confirmPasswordError =
      !doPasswordsMatch() && formData.confirmPassword.length > 0;

    return (
      <>
        <h2 className="font-bold text-2xl mb-2">Enter Password</h2>
        <p className="text-base-coolGray mb-6">
          Great! Now, enter your password to complete your account setup
        </p>
        <div className="flex gap-4 flex-col">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="8+ characters"
                className={`w-full bg-base-seasalt rounded py-3 px-4 pr-10 min-w-[400px] ${
                  passwordError
                    ? 'border border-red-500 outline-none'
                    : 'outline-brand-baseBlue outline-1'
                }`}
                value={formData.password}
                onChange={(e) => updateFormData('password', e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d={
                      showPassword
                        ? 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21'
                        : 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                    }
                  />
                </svg>
              </button>
            </div>
            {passwordError && (
              <p className="text-red-500 text-sm mt-1 text-left">
                Weak password.
              </p>
            )}

            <div className="space-y-2 mt-3">
              <div className={`flex items-center gap-2 text-sm ${passwordValidation.minLength ? 'text-green-600' : 'text-red-500'}`}>
                {passwordValidation.minLength ? '✓' : '✗'} Minimum 8 characters
              </div>
              <div className={`flex items-center gap-2 text-sm ${passwordValidation.hasLowercase ? 'text-green-600' : 'text-red-500'}`}>
                {passwordValidation.hasLowercase ? '✓' : '✗'} At least one lowercase letter
              </div>
              <div className={`flex items-center gap-2 text-sm ${passwordValidation.hasUppercase ? 'text-green-600' : 'text-red-500'}`}>
                {passwordValidation.hasUppercase ? '✓' : '✗'} At least one uppercase letter
              </div>
              <div className={`flex items-center gap-2 text-sm ${passwordValidation.hasNumber ? 'text-green-600' : 'text-red-500'}`}>
                {passwordValidation.hasNumber ? '✓' : '✗'} At least one number
              </div>
              <div className={`flex items-center gap-2 text-sm ${passwordValidation.hasSpecialChar ? 'text-green-600' : 'text-red-500'}`}>
                {passwordValidation.hasSpecialChar ? '✓' : '✗'} At least one special character
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="8+ characters"
                className={`w-full bg-base-seasalt rounded py-3 px-4 pr-10 min-w-[400px] ${
                  confirmPasswordError
                    ? 'border border-red-500 outline-none'
                    : 'outline-brand-baseBlue outline-1'
                }`}
                value={formData.confirmPassword}
                onChange={(e) => updateFormData('confirmPassword', e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d={
                      showConfirmPassword
                        ? 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21'
                        : 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                    }
                  />
                </svg>
              </button>
            </div>
            {confirmPasswordError && (
              <p className="text-red-500 text-sm mt-1 text-left">
                Passwords do not match
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="terms-invite"
              className="rounded"
              checked={formData.terms}
              onChange={(e) => updateFormData('terms', e.target.checked)}
            />
            <label htmlFor="terms-invite" className="text-sm text-gray-600">
              I agree to{' '}
              <Link to="/terms-of-service" className="text-brand-baseBlue underline" target="_blank">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy-policy" className="text-brand-baseBlue underline" target="_blank">
                Privacy Policy
              </Link>
            </label>
          </div>

          <Button
            onClick={handleNextStep}
            className="w-full min-w-[400px]"
            disabled={!isStep2Valid() || !formData.terms}
          >
            Continue
          </Button>
          <button
            onClick={handlePreviousStep}
            className="w-full min-w-[400px] bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <span>←</span>
            Previous Step
          </button>
        </div>
      </>
    );
  };

  const renderInviteComplete = () => {
    // This step auto-redirects to accept invite after email verification
    const token = localStorage.getItem('pendingInviteToken');
    if (token) {
      // Use setTimeout to avoid immediate navigation during render
      setTimeout(() => {
        navigate(`/accept-invite/${token}`);
      }, 100);
    }

    return (
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-baseBlue mx-auto mb-4"></div>
        <p className="text-base-coolGray">Redirecting to accept invitation...</p>
      </div>
    );
  };

  const renderStep3 = () => {
    const businessNameError = getBusinessNameError();
    const businessAddressError = getBusinessAddressError();
    const businessCategoryError = getBusinessCategoryError();
    const teamSizeError = getTeamSizeError();
    const termsError = getTermsError();

    return (
      <>
        <h2 className="font-bold text-2xl mb-2">Business Information</h2>
        <p className="text-base-coolGray mb-6">
          Tell us a bit about your business. We'll autofill your details from
          Google Business listings to save time. This helps us tailor our
          services to your needs.
        </p>
        <div className="flex gap-4 flex-col">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Business Name
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="e.g. Luskin's"
                className={`w-full bg-base-seasalt rounded py-3 px-4 ${
                  businessNameError
                    ? 'border border-red-500 outline-none'
                    : 'outline-brand-baseBlue outline-1'
                }`}
                value={formData.businessName}
                onChange={(e) => handleBusinessNameChange(e.target.value)}
              />
              {businessNameError && (
                <p className="text-red-500 text-sm mt-1 text-left">
                  {businessNameError}
                </p>
              )}
              {isSearchingPlaces && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-baseBlue"></div>
                </div>
              )}
            </div>

            {/* Places dropdown */}
            {showPlacesDropdown && places.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {places.map((place, index) => (
                  <div
                    key={index}
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    onClick={() => handlePlaceSelect(place)}
                  >
                    <div className="font-medium text-gray-900">
                      {place.name}
                    </div>
                    <div className="text-sm text-gray-600">{place.address}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Business Email
            </label>
            <div className="relative">
              <input
                type="email"
                placeholder="e.g. contact@yourbusiness.com"
                className={`w-full bg-base-seasalt rounded py-3 px-4 ${
                  formData.businessEmail &&
                  !isValidEmail(formData.businessEmail)
                    ? 'border border-red-500 outline-none'
                    : 'outline-brand-baseBlue outline-1'
                }`}
                value={formData.businessEmail}
                onChange={(e) =>
                  updateFormData('businessEmail', e.target.value)
                }
              />
              {formData.businessEmail &&
                !isValidEmail(formData.businessEmail) && (
                  <p className="text-red-500 text-sm mt-1 text-left">
                    Please enter a valid email address
                  </p>
                )}
              {businessEmailError && (
                <p className="text-red-500 text-sm mt-1 text-left">
                  {businessEmailError}
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Business Address
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="e.g. 1341 Poplar Street, Chicago, IL 60606"
                className={`w-full bg-base-seasalt rounded py-3 px-4 ${
                  businessAddressError
                    ? 'border border-red-500 outline-none'
                    : 'outline-brand-baseBlue outline-1'
                }`}
                value={formData.businessAddress}
                onChange={(e) =>
                  updateFormData('businessAddress', e.target.value)
                }
              />
              {businessAddressError && (
                <p className="text-red-500 text-sm mt-1 text-left">
                  {businessAddressError}
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Industry
            </label>
            <select
              className={`w-full bg-base-seasalt rounded py-3 px-4 ${
                businessCategoryError
                  ? 'border border-red-500 outline-none'
                  : 'outline-brand-baseBlue outline-1'
              } ${
                formData.businessCategory === ''
                  ? 'text-gray-400'
                  : 'text-gray-900'
              }`}
              value={formData.businessCategory}
              onChange={(e) =>
                updateFormData('businessCategory', e.target.value)
              }
            >
              <option value="" disabled>
                Select Company Type
              </option>
              {(
                businessCategories as {
                  categories: BusinessCategoryInterface[];
                }
              )?.categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {businessCategoryError && (
              <p className="text-red-500 text-sm mt-1 text-left">
                {businessCategoryError}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Team Size
            </label>
            <select
              className={`w-full bg-base-seasalt rounded py-3 px-4 ${
                teamSizeError
                  ? 'border border-red-500 outline-none'
                  : 'outline-brand-baseBlue outline-1'
              } ${
                formData.teamSize === '' ? 'text-gray-400' : 'text-gray-900'
              }`}
              value={formData.teamSize}
              onChange={(e) => updateFormData('teamSize', e.target.value)}
            >
              <option value="" disabled>
                Select Team Size
              </option>
              <option value="1-5">1-5 employees</option>
              <option value="6-10">6-10 employees</option>
              <option value="11-25">11-25 employees</option>
              <option value="26-50">26-50 employees</option>
              <option value="50+">50+ employees</option>
            </select>
            {teamSizeError && (
              <p className="text-red-500 text-sm mt-1 text-left">
                {teamSizeError}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="terms"
              className="rounded"
              checked={formData.terms}
              onChange={(e) => updateFormData('terms', e.target.checked)}
            />
            <label htmlFor="terms" className="text-sm text-gray-600">
              I agree to{' '}
              <Link
                to="/terms-of-service"
                className="text-brand-baseBlue underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy-policy"
                className="text-brand-baseBlue underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </Link>
              .
            </label>
          </div>
          {termsError && (
            <p className="text-red-500 text-sm mt-1 text-left">{termsError}</p>
          )}
          <Button
            onClick={handleFinalSubmit}
            className="w-full"
            loading={isValidatingBusinessEmail}
            disabled={
              isValidatingBusinessEmail ||
              !!businessEmailError ||
              !formData.businessName.trim() ||
              !formData.businessEmail.trim() ||
              !isValidEmail(formData.businessEmail) ||
              !formData.businessCategory ||
              !formData.businessAddress.trim() ||
              !formData.teamSize ||
              !formData.terms
            }
          >
            Continue
          </Button>
          <button
            onClick={handlePreviousStep}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <span>←</span>
            Previous Step
          </button>
        </div>
      </>
    );
  };

  const renderStep4 = () => (
    <>
      <h2 className="font-bold text-2xl mb-2">You're almost there!</h2>
      <p className="text-base-coolGray mb-6">
        One quick step before choosing your plan
      </p>

      <div className="flex justify-center mb-6">
        <div className="flex items-center justify-center m-6">
          <NoCampaignsIcon width={110} height={110} />
        </div>
      </div>

      <div className="text-center mb-6">
        <p className="text-base-coolGray mb-2">
          We've sent a confirmation link to
        </p>
        <p className="text-base-coolGray mb-2">
          <span className="font-bold text-gray-800">{formData.email}</span>.
          Please check your inbox and click the link to verify your email
          address.
        </p>
      </div>

      <div className="text-center mb-6">
        <p className="text-sm text-gray-500 mb-4">
          Didn't receive anything? Check your spam and junk folder or resend
          email{' '}
          {countdown > 0 && (
            <>
              in <span className="font-bold">{formatCountdown(countdown)}</span>
            </>
          )}
        </p>
        <Button
          kind="primary"
          className={`w-full mb-4 ${countdown > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={handleResendEmail}
          disabled={countdown > 0}
        >
          Resend Email
        </Button>
        <button
          onClick={handlePreviousStep}
          className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
        >
          <span>←</span>
          Previous Step
        </button>
      </div>
    </>
  );

  const renderStep5 = () => (
    <>
      <h2 className="font-bold text-3xl mb-2">Try Echo Ratings Risk Free</h2>
      <p className="text-base-coolGray mb-6 max-w-2xl mx-auto">
        Explore Echo Ratings for 14 days, risk free. No credit card required.
        Access features on the Pro plan until <strong>14 days</strong>. Upgrade
        at anytime.
      </p>

      {/* Tab Navigation */}
      <div className="flex justify-center gap-4 mb-8">
        <button
          onClick={() => setActiveTab('plan')}
          className={`relative px-6 py-3 rounded-lg font-medium transition-colors ${
            activeTab === 'plan'
              ? 'bg-brand-baseBlue text-white border border-brand-baseBlue'
              : 'bg-white text-gray-600 border border-gray-200'
          }`}
        >
          Start with a Plan
          {activeTab === 'plan' && (
            <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
              Save 10%
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('free')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            activeTab === 'free'
              ? 'bg-brand-baseBlue text-white border border-brand-baseBlue'
              : 'bg-white text-gray-600 border border-gray-200'
          }`}
        >
          Try for Free (No Card Needed)
        </button>
      </div>

      {activeTab === 'plan' ? (
        <>
          <p className="text-base-coolGray mb-8">
            Get 14 days free + 10% off when you choose a plan now and add
            billing info.
          </p>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white border rounded-lg p-6 shadow-sm relative cursor-pointer transition-all duration-200 ${
                  selectedPlan === plan.id
                    ? 'border-brand-baseBlue shadow-lg'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-brand-baseBlue text-white text-xs px-3 py-1 rounded-full">
                      Most Popular Plan
                    </span>
                  </div>
                )}
                {plan.bestValue && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gray-500 text-white text-xs px-3 py-1 rounded-full">
                      Best Value
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    {plan.price}
                  </div>
                  <div className="text-lg font-semibold text-gray-900 mb-2">
                    {plan.name}
                  </div>
                  <div className="text-sm text-gray-600">{plan.audience}</div>
                </div>

                <ul className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      {feature.included ? (
                        <svg
                          className="w-5 h-5 text-green-500 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5 text-red-500 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      <span
                        className={`text-sm ${feature.included ? 'text-gray-700' : 'text-gray-500'}`}
                      >
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="text-center mb-12">
          <h3 className="text-xl font-semibold mb-4">Start Your Free Trial</h3>
          <p className="text-base-coolGray max-w-md mx-auto">
            No credit card required. Get full access to all features for 14
            days, completely free.
          </p>
        </div>
      )}

      {/* Paid Features Section */}
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold mb-2 text-brand-baseBlue">
          Paid features to explore
        </h3>
        <p className="text-base-coolGray">First 14 days free</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {WELCOME_FEATURES.map((feature, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-6 text-center">
            <div className="flex justify-center mb-4">{feature.icon}</div>
            <h4 className="font-semibold text-sm mb-2">{feature.title}</h4>
            <p className="text-xs text-base-coolGray">{feature.description}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col items-center gap-4 w-full">
        <Button
          onClick={async () => {
            // Store form data in localStorage before navigating to welcome page
            localStorage.setItem('signupFormData', JSON.stringify(formData));
            // Navigate to welcome page for plan selection and account creation
            navigate('/welcome');
          }}
          className="w-full bg-blue-400 hover:bg-blue-500 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
        >
          Start 14-Day Free Trial
        </Button>

        <button
          onClick={handlePreviousStep}
          className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
        >
          <span>←</span>
          Previous Step
        </button>
      </div>
    </>
  );

  const renderCurrentStep = () => {
    if (isInviteSignup) {
      // Invite signup flow: 1 (account) -> 2 (password) -> 3 (email verify) -> 4 (done/redirect)
      switch (currentStep) {
        case 1:
          return renderStep1();
        case 2:
          return renderStep2Invite(); // Modified step 2 for invite flow
        case 3:
          return renderStep4(); // Email verification
        case 4:
          return renderInviteComplete(); // Redirect to accept invite
        default:
          return renderStep1();
      }
    }

    // Normal signup flow
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      default:
        return renderStep1();
    }
  };

  return (
    <div className="flex items-center justify-center flex-col min-h-screen py-6">
      <Logo width={139} height={110} className="mb-8" />
      <div
        className={`bg-white text-center space-y-4 rounded px-10 pt-10 pb-6 ${
          currentStep === 5 ? 'max-w-[1200px]' : 'max-w-[510px]'
        }`}
      >
        {renderProgressBar()}
        {renderCurrentStep()}
      </div>
      <p className="mt-10 text-base-coolGray">
        Have an account?{' '}
        <Link to="/login" className="text-brand-baseBlue font-bold">
          Log In
        </Link>
      </p>
    </div>
  );
};
